import React, { useMemo } from 'react';
import { BookingPill } from '../../BookingPill';
import { EmptyState } from '../../EmptyState';
import { StatusMenu } from '../StatusMenu';
import {
  colourFor,
  statusClassFor,
  fmtTime,
  fmtMoney,
} from '../../../lib/diary/helpers';

// ===== TODAY QUEUE VIEW =====
// Mobile-first chronological list of the day's bookings. Renders a
// "Now" ribbon between the currently-running booking and the next one;
// each row has a status badge, payment pill, and status kebab menu.
export const TodayView = ({
  bookings, isFirstForCustomer, onStatusChanged, onOpenBooking,
  isBarber, myBarberId, myPerms, focusBookingId,
}) => {
  const now = new Date();
  const sorted = useMemo(
    () => [...bookings].sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at)),
    [bookings]
  );
  if (sorted.length === 0) {
    return (
      <div className="today-empty" data-testid="today-empty">
        <EmptyState
          title="Nothing on today"
          detail="No bookings on this day yet."
          dataTestId="today-empty-state"
          compact
        />
      </div>
    );
  }
  // Find first booking that hasn't ended — that's "now or next".
  const upcomingIx = sorted.findIndex(b => new Date(b.ends_at) > now);
  const ribbonIx = upcomingIx === -1 ? sorted.length : upcomingIx;

  return (
    <div className="today" data-testid="cal-today">
      {sorted.map((b, i) => {
        const isRunning = new Date(b.starts_at) <= now && new Date(b.ends_at) > now;
        const showRibbon = i === ribbonIx && !isRunning;
        return (
          <React.Fragment key={b.id}>
            {showRibbon && (
              <div className="today-ribbon" data-testid="today-ribbon"><span>Next up</span></div>
            )}
            {isRunning && (
              <div className="today-ribbon now" data-testid="today-ribbon-now"><span>Now</span></div>
            )}
            <div
              className={`today-row ${colourFor(b, isFirstForCustomer(b))}${isRunning ? ' is-running' : ''}${focusBookingId === b.id ? ' ev-focus' : ''} ${statusClassFor(b)}`}
              data-testid={`today-row-${b.id}`}
              onClick={() => onOpenBooking?.(b.id)}
              style={{ cursor: 'pointer' }}
            >
              <div className="today-time">
                <div className="t-hh">{fmtTime(b.starts_at)}</div>
                <div className="t-sm">{Math.round((new Date(b.ends_at) - new Date(b.starts_at)) / 60000)} min</div>
              </div>
              <div className="today-body">
                <div className="today-line1">
                  <b>{b.customer?.name}</b>
                  <div className="today-pills">
                    <BookingPill booking={b} testIdSuffix={b.id} />
                  </div>
                </div>
                <div className="today-line2">
                  {b.service?.name} · {b.barber?.name?.split(' ')[0]} · {fmtMoney(b.service?.price)}
                </div>
              </div>
              <StatusMenu
                booking={b}
                onChanged={onStatusChanged}
                canManage={!isBarber || b.barber_id === myBarberId}
                perms={isBarber ? myPerms : undefined}
              />
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
};
